import React, { useState, useEffect, useCallback } from 'react';
import { Search, Users, TrendingUp, Award, Filter, Menu } from 'lucide-react';
import StudentCard from './components/StudentCard';
import StatsCard from './components/StatsCard';
import SearchBar from './components/SearchBar';
import { fetchAcademicDetails } from '../../_services/academic.service';
// import { fetchAcademicDetailsMock as fetchAcademicDetails } from '../../_services/academic-mock.service';
import type { AcademicRecord } from '@conceptwaves/shared-components';
import Sidebar from '../../components/Sidebar/Sidebar';
import './styles.css';

const AcademicDashboard: React.FC = () => {
  const [records, setRecords] = useState<AcademicRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  });

  const loadData = async (search = '', offset = 0, limit = 50) => {
    try {
      setLoading(true);
      const response = await fetchAcademicDetails({
        limit,
        offset,
        search,
      });

      setRecords(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error loading academic details:', error);
      // Set empty data on error to show "No results" message
      setRecords([]);
      setPagination({
        total: 0,
        limit: 50,
        offset: 0,
        hasMore: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData('', 0, 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    loadData(query, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMore = () => {
    loadData(searchQuery, pagination.offset + pagination.limit);
  };

  // Calculate statistics
  const totalStudents = pagination.total;
  const studentsWithTests = records.filter(r => r.TestInfo && r.TestInfo.length > 0).length;
  const totalTests = records.reduce((sum, r) => sum + (r.TestInfo?.length || 0), 0);
  const avgScore = records
    .filter(r => r.TestInfo && r.TestInfo.length > 0)
    .reduce((sum, r) => {
      const studentAvg = r.TestInfo!.reduce((testSum, test) => {
        const percentage = (test.totalScored / test.totalMaxMarks) * 100;
        return testSum + (isNaN(percentage) ? 0 : percentage);
      }, 0) / (r.TestInfo?.length || 1);
      return sum + studentAvg;
    }, 0) / (studentsWithTests || 1);

  return (
    <>
    <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
    <div className="academic-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <button
          className="mobile-menu-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          title="Toggle menu"
        >
          <Menu size={20} />
        </button>
        <div className="header-content">
          <div className="header-text">
            <div>
              <h1 className="dashboard-title">Academic Dashboard</h1>
              <p className="dashboard-subtitle">Student performance overview and analytics</p>
            </div>
            <div className="header-actions">
              <button className="filter-button">
                <Filter size={18} />
                Filters
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="search-section">
            <SearchBar onSearch={handleSearch} placeholder="Search by name, email, or mobile..." />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="dashboard-content">
        {/* Stats Grid */}
        <div className="stats-grid">
          <StatsCard
            title="Total Students"
            value={totalStudents.toString()}
            icon={<Users size={24} />}
            color="blue"
          />
          <StatsCard
            title="Active Tests"
            value={totalTests.toString()}
            icon={<Award size={24} />}
            color="green"
          />
          <StatsCard
            title="Students with Tests"
            value={studentsWithTests.toString()}
            icon={<TrendingUp size={24} />}
            color="purple"
          />
          <StatsCard
            title="Average Score"
            value={`${avgScore.toFixed(1)}%`}
            icon={<Award size={24} />}
            color="orange"
          />
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        )}

        {/* Student Cards Grid */}
        {!loading && (
          <>
            <div className="cards-grid">
              {records.map((record) => (
                <StudentCard key={record.Id} record={record} />
              ))}
            </div>

            {/* Load More Button */}
            {pagination.hasMore && (
              <div className="load-more-container">
                <button onClick={handleLoadMore} className="load-more-button">
                  Load More
                </button>
              </div>
            )}

            {/* No Results */}
            {records.length === 0 && (
              <div className="no-results">
                <Search size={48} className="no-results-icon" />
                <h3 className="no-results-title">No results found</h3>
                <p className="no-results-text">Try adjusting your search query</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </>
  );
};

export default AcademicDashboard;
